import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { useMutation, useQuery } from "@apollo/client";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import {
  ChipWrap,
  DateStamp,
} from "../../../../platform/careComponents/helper/sharedFun";
import {
  getPricingFlagsFromExtra,
  getResidentLabel,
  formatUSD,
  mapKind,
  mapPayerType,
  mapUrgency,
} from "../../../../platform/careComponents/helper/referralHelpers";
import { GET_BOOKINGS_BY_EMAIL } from "../../../../platform/graphql/queries/booking";
import { GET_INSTALLMENTS_BY_LEAD } from "../../../../platform/graphql/queries/care";
import { GET_BUSINESS_BOOKING_TYPES } from "../../../../platform/graphql/queries/bookingType";
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations";
import { UPDATE_CONCIERGE_LEAD_FOLLOW_UP } from "../../../../platform/graphql/mutations/care";
import { REMOVE_BOOKINGS } from "../../../../platform/graphql/mutations/generic";
import { ProviderContactPanel } from "../../../../platform/careComponents/components/providerContactPanel";
import SettlementInline from "../../../../platform/careComponents/components/settlementInline";
import { ReferrerContactPanel } from "../../../../platform/careComponents/components/referrerContactPanel";
import TourBookingButton, {
  BusinessBookingConfig,
} from "../../../../platform/careComponents/components/tourBookingButton";
import ReferrerReadyToggle from "../../../../platform/careComponents/components/referrerReadyToggle";
import {
  getBookingActionHtmlBlock,
  getBookingActionLinks,
  getBookingActionTextBlock,
} from "../../../../apps/plany-care/integrations/booking/bookingEmailActions";
import {
  STAGE_LABELS,
  TERMINAL_STAGE_SET,
  getFollowUpSummary,
  getLeadBroadcastGroupId,
  parseExtra,
} from "./conciergeBoardShared";

const ACTIVE_STAGE_FLOW = [
  "NEW",
  "QUALIFYING",
  "MATCHING",
  "TOUR_SCHEDULED",
  "PENDING_DECISION",
  "PLACED",
] as const;
const PRE_PLACEMENT_STAGE_FLOW = ACTIVE_STAGE_FLOW.filter(
  (step) => step !== "PLACED"
);
function getLeadCardTone(status?: string | null) {
  switch (String(status || "").toUpperCase()) {
    case "NEW":
      return {
        headerBg: "rgba(248, 251, 255, 0.96)",
        sectionBg: "#ffffff",
      };
    case "PLACED":
      return {
        headerBg: "rgba(248, 252, 249, 0.96)",
        sectionBg: "#ffffff",
      };
    case "LOST":
    case "CANCELLED":
    case "CLOSED":
    case "WITHDRAWN":
      return {
        headerBg: "rgba(250, 250, 250, 0.96)",
        sectionBg: "#ffffff",
      };
    default:
      return {
        headerBg: "rgba(248, 250, 252, 0.96)",
        sectionBg: "#ffffff",
      };
  }
}

type InstallmentRow = {
  id?: string;
  status?: "DUE" | "PAID" | "FAILED" | "CANCELLED";
  dueAmount?: number | string;
  paidAmount?: number | string;
  dueAt?: string | null;
};

function toDateTimeLocalInput(value: string | null | undefined) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hour = String(dt.getHours()).padStart(2, "0");
  const minute = String(dt.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function formatMeetingTimeForNotice(rawTime: string | null | undefined, timeZone?: string | null) {
  const date = rawTime ? new Date(rawTime) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

function toEmailList(value: any): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => String(item || "").trim().toLowerCase())
          .filter(Boolean);
      }
    } catch {
      // ignore and fall back to splitting
    }
    return raw
      .split(/[,\n;]/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

function buildAgentAgreementUrl(rootClientPath: string, options: { leadId?: string | number | null; groupId?: string | number | null }) {
  const params = new URLSearchParams();
  if (options.leadId != null) params.set("lead", String(options.leadId));
  if (options.groupId != null) params.set("group", String(options.groupId));
  return `${rootClientPath}/care/agent/agreement?${params.toString()}`;
}

function findScheduledTourBookingForLead(
  lead: any,
  bookings: any[]
): {
  id?: string | number | null;
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  if (!Array.isArray(bookings) || bookings.length === 0) return null;
  const leadId = String(lead?.id || "").trim();
  const extra = parseExtra(lead?.extra) || {};
  const scheduledBookingId = String(extra?.tourSchedule?.bookingId || "").trim();
  const conciergeMirrorBookingId = String(
    extra?.tourSchedule?.conciergeMirrorBookingId || ""
  ).trim();
  const providerName =
    String(extra?.tourSchedule?.businessName || lead?.business?.name || "").trim() ||
    null;
  if (!leadId && !scheduledBookingId && !conciergeMirrorBookingId) return null;
  const now = Date.now();
  const matchesStoredBooking = (booking: any) => {
    const bookingId = String(booking?.id || "").trim();
    const externalBookingId = String(booking?.bookingId || "").trim();
    return (
      (!!scheduledBookingId && bookingId === scheduledBookingId) ||
      (!!conciergeMirrorBookingId && bookingId === conciergeMirrorBookingId) ||
      (!!scheduledBookingId && externalBookingId === `coord:${scheduledBookingId}`)
    );
  };
  const activeBookings = bookings.filter((booking: any) => {
    if (Number(booking?.status) === 0) return false;
    const whenLabel = formatTourScheduleLabel(String(booking?.time || "").trim());
    if (!whenLabel) return false;
    const when = Date.parse(String(booking?.time || "").trim());
    if (!Number.isFinite(when)) return false;
    return when >= now - 24 * 60 * 60 * 1000;
  });
  const storedMatch =
    scheduledBookingId || conciergeMirrorBookingId
      ? activeBookings.find((booking: any) => matchesStoredBooking(booking)) || null
      : null;
  const leadMatch = activeBookings.find((booking: any) => {
    const referralLeadId = String(booking?.referralLeadId || "").trim();
    if (referralLeadId === leadId) {
      const when = formatTourScheduleLabel(String(booking?.time || "").trim());
      return !!when;
    }
    const notes = String(booking?.quickNotes || "").trim();
    const leadMatch = notes.match(/(?:referral|tour\s+for)\s+lead\s*#(\d+)/i);
    return !!leadMatch?.[1] && String(leadMatch[1]).trim() === leadId;
  });
  const match = storedMatch || leadMatch || null;
  if (!match) return null;
  return {
    id: match?.id ?? null,
    bookingTime: String(match?.time || "").trim() || null,
    businessName: providerName,
  };
}

function getTourScheduleFromLead(lead: any): {
  id?: string | number | null;
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  const status = String(lead?.status || "").toUpperCase();
  const extra = parseExtra(lead?.extra) || {};
  const tourSchedule = extra?.tourSchedule;
  const bookingTime = String(tourSchedule?.bookingTime || "").trim();
  if (!bookingTime && status !== "TOUR_SCHEDULED") return null;
  return {
    id:
      String(
        tourSchedule?.bookingId || tourSchedule?.conciergeMirrorBookingId || "",
      ).trim() || null,
    bookingTime: bookingTime || null,
    businessName:
      String(tourSchedule?.businessName || lead?.business?.name || "").trim() ||
      null,
  };
}

function getLatestTourCancellation(lead: any): {
  canceledAt?: string | null;
  canceledByName?: string | null;
  canceledByRole?: string | null;
  businessName?: string | null;
  bookingTime?: string | null;
} | null {
  const extra = parseExtra(lead?.extra) || {};
  const cancellation = extra?.lastTourCancellation;
  if (!cancellation) return null;
  return {
    canceledAt: String(cancellation?.canceledAt || "").trim() || null,
    canceledByName: String(cancellation?.canceledByName || "").trim() || null,
    canceledByRole: String(cancellation?.canceledByRole || "").trim() || null,
    businessName:
      String(cancellation?.businessName || lead?.business?.name || "").trim() || null,
    bookingTime: String(cancellation?.bookingTime || "").trim() || null,
  };
}

function formatTourScheduleLabel(bookingTime?: string | null): string | null {
  if (!bookingTime) return null;
  const trimmed = String(bookingTime).trim();
  const numeric = Number(trimmed);
  const dt =
    !Number.isNaN(numeric) && trimmed !== ""
      ? new Date(numeric > 1e12 ? numeric : numeric * 1000)
      : new Date(trimmed);
  if (Number.isNaN(dt.getTime())) return null;
  return dt
    .toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());
}

function ReferralStageTracker({ status }: { status?: string | null }) {
  const normalized = String(status || "").toUpperCase();
  const isTerminal = TERMINAL_STAGE_SET.has(normalized);
  const flow = isTerminal ? [...PRE_PLACEMENT_STAGE_FLOW, normalized] : [...ACTIVE_STAGE_FLOW];
  const matchedIndex = flow.findIndex((step) => step === normalized);
  const activeIndex = matchedIndex >= 0 ? matchedIndex : 0;

  return (
    <Box sx={{ overflowX: "auto", pb: 0.25 }}>
      <Stack
        direction="row"
        spacing={0}
        alignItems="flex-start"
        sx={{ minWidth: 560, pt: 0.25 }}
      >
        {flow.map((step, index) => {
          const reached = index <= activeIndex;
          const isCurrent = index === activeIndex;
          const isTerminalStep = TERMINAL_STAGE_SET.has(step);
          const nodeColor = isTerminalStep ? "error.main" : "success.main";
          const inactiveColor = "grey.300";
          const connectorColor =
            index < activeIndex
              ? index + 1 === activeIndex && isTerminal
                ? "error.main"
                : "success.main"
              : inactiveColor;

          return (
            <React.Fragment key={`${step}-${index}`}>
              <Stack alignItems="center" spacing={0.75} sx={{ minWidth: 82 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: 2,
                    borderColor: reached ? nodeColor : "grey.400",
                    bgcolor: reached ? nodeColor : inactiveColor,
                    color: reached ? "common.white" : "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    boxShadow: isCurrent
                      ? (t) =>
                          `0 0 0 4px ${alpha(
                            isTerminalStep ? t.palette.error.main : t.palette.success.main,
                            0.18
                          )}`
                      : "none",
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    textAlign: "center",
                    color: reached
                      ? isTerminalStep
                        ? "error.main"
                        : isCurrent
                        ? "success.main"
                        : "text.primary"
                      : "text.secondary",
                    fontWeight: isCurrent ? 700 : 500,
                    lineHeight: 1.2,
                  }}
                >
                  {STAGE_LABELS[step] || step}
                </Typography>
              </Stack>
              {index < flow.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 28,
                    mt: 1.75,
                    mx: 0.5,
                    borderTop: 3,
                    borderColor: connectorColor,
                    opacity: index < activeIndex ? 1 : 0.85,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

function useInstallmentSnapshot(leadId: string | number) {
  const { data, loading } = useQuery(GET_INSTALLMENTS_BY_LEAD, {
    variables: { leadId: String(leadId) },
    fetchPolicy: "cache-first",
  });

  const rows: InstallmentRow[] = (data?.referralInstallments ?? []) as any[];
  const total = rows.reduce((s, r) => s + Number(r?.dueAmount || 0), 0);
  const paid = rows.reduce((s, r) => s + Number(r?.paidAmount || 0), 0);
  const remaining = Math.max(0, total - paid);
  const hasSchedule = rows.length > 0;

  const allPaid = hasSchedule
    ? rows.every(
        (r) =>
          r?.status === "PAID" ||
          Number(r?.paidAmount || 0) >= Number(r?.dueAmount || 0)
      )
    : false;

  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);

  const notFullyPaid = (r: InstallmentRow) =>
    Number(r?.paidAmount || 0) < Number(r?.dueAmount || 0) &&
    r?.status !== "PAID" &&
    r?.status !== "CANCELLED";

  const parsedDue = (s?: string | null) => (s ? Date.parse(String(s)) : NaN);

  const overdueItems = rows.filter(
    (r) =>
      notFullyPaid(r) &&
      Number.isFinite(parsedDue(r.dueAt)) &&
      parsedDue(r.dueAt) < startOfToday.getTime()
  );
  const dueTodayItems = rows.filter(
    (r) =>
      notFullyPaid(r) &&
      Number.isFinite(parsedDue(r.dueAt)) &&
      parsedDue(r.dueAt) >= startOfToday.getTime() &&
      parsedDue(r.dueAt) < endOfToday.getTime()
  );

  const overdueCount = overdueItems.length;
  const earliestOverdueTs = overdueItems
    .map((r) => parsedDue(r.dueAt))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)[0];
  const daysOverdue = Number.isFinite(earliestOverdueTs)
    ? Math.max(
        1,
        Math.ceil((now - Number(earliestOverdueTs)) / (1000 * 60 * 60 * 24))
      )
    : 0;
  const nextDueTs = rows
    .map((r) => parsedDue(r.dueAt))
    .filter((t) => Number.isFinite(t) && t >= startOfToday.getTime())
    .sort((a, b) => a - b)[0];

  return {
    loading,
    hasSchedule,
    total,
    paid,
    remaining,
    allPaid,
    overdueCount,
    daysOverdue,
    earliestOverdue: Number.isFinite(earliestOverdueTs)
      ? new Date(earliestOverdueTs)
      : null,
    dueTodayCount: dueTodayItems.length,
    nextDueAt: Number.isFinite(nextDueTs) ? new Date(nextDueTs) : null,
  };
}

function PaymentStatusInline({
  leadId,
  leadStatus,
}: {
  leadId: string | number;
  leadStatus: string;
}) {
  const snap = useInstallmentSnapshot(leadId);

  if (snap.loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" variant="outlined" label="Payments • Checking..." />
      </Stack>
    );
  }
  if (!snap.hasSchedule) {
    if (leadStatus !== "PLACED") return null;
    return <Chip size="small" variant="outlined" label="No Payment Schedule" />;
  }
  if (snap.overdueCount > 0) {
    return (
      <Alert severity="error" variant="filled" sx={{ borderRadius: 1.5 }}>
        <Stack spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Payment overdue
          </Typography>
          <Typography variant="body2">
            Paid {formatUSD(snap.paid)} of {formatUSD(snap.total)} • Remaining{" "}
            {formatUSD(snap.remaining)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="error" label={`${snap.overdueCount} overdue`} />
            {snap.earliestOverdue ? (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`Oldest: ${snap.earliestOverdue.toLocaleDateString()} (${snap.daysOverdue} day${
                  snap.daysOverdue > 1 ? "s" : ""
                })`}
              />
            ) : null}
            {snap.nextDueAt ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Next due: ${snap.nextDueAt.toLocaleDateString()}`}
              />
            ) : null}
          </Stack>
          <Typography variant="caption">
            Please follow up with the provider to resolve overdue balance.
          </Typography>
        </Stack>
      </Alert>
    );
  }
  if (snap.dueTodayCount > 0) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Payment due today
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${snap.dueTodayCount} installment${snap.dueTodayCount > 1 ? "s" : ""}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Remaining ${formatUSD(snap.remaining)}`}
            />
          </Stack>
        </Stack>
      </Alert>
    );
  }

  const label =
    snap.allPaid || snap.remaining <= 0
      ? "Settled"
      : snap.paid > 0
      ? "Payment Incomplete"
      : "Payment Pending";
  const color =
    snap.allPaid || snap.remaining <= 0
      ? "success"
      : snap.paid > 0
      ? "warning"
      : "info";

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Chip size="small" color={color as any} variant="outlined" label={`Payment • ${label}`} />
      <Chip
        size="small"
        variant="outlined"
        label={`Paid ${formatUSD(snap.paid)} / ${formatUSD(snap.total)}`}
      />
      {snap.nextDueAt ? (
        <Chip
          size="small"
          variant="outlined"
          label={`Next due: ${snap.nextDueAt.toLocaleDateString()}`}
        />
      ) : null}
    </Stack>
  );
}

type Props = {
  l: any;
  currentAgentId?: string | number | null;
  canActAsAgent?: boolean;
  rootClientPath: string;
  forceTourReady?: boolean;
  onTourEnabled?: () => void;
  agentOptions?: Array<{ id: string; name: string; email?: string | null }>;
  suppressGroupContext?: boolean;
};

export default function ConciergeCityBoardLeadRow({
  l,
  currentAgentId,
  canActAsAgent = true,
  rootClientPath,
  forceTourReady = false,
  onTourEnabled,
  agentOptions = [],
  suppressGroupContext = false,
}: Props) {
  const leadUiKey = React.useMemo(() => String(l?.id || "unknown"), [l?.id]);
  const referrerPanelStorageKey = `conciergeLead:${leadUiKey}:referrerPanelOpen`;
  const providerPanelStorageKey = `conciergeLead:${leadUiKey}:providerPanelOpen`;
  const [referrerPanelOpen, setReferrerPanelOpen] = React.useState(true);
  const [providerPanelOpen, setProviderPanelOpen] = React.useState(true);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [updateFollowUp, { loading: updatingFollowUp }] = useMutation(
    UPDATE_CONCIERGE_LEAD_FOLLOW_UP,
  );
  const [removeBookingMutation, { loading: removingBooking }] = useMutation(REMOVE_BOOKINGS, {
    update(cache, { data }) {
      const removedBooking = data?.removeBooking;
      const normalizedId = removedBooking ? cache.identify(removedBooking) : null;
      if (normalizedId) {
        cache.evict({ id: normalizedId });
        cache.gc();
      }
    },
  });
  const isProd = process.env.NODE_ENV === "production";
  const bookingLookupPollMs = isProd ? 15000 : 5000;
  const { providerUser } = React.useContext(UserContext);
  const residentLabel = getResidentLabel(l);
  const [transferOpen, setTransferOpen] = React.useState(false);
  const [transferTargetId, setTransferTargetId] = React.useState("");
  const [transferNote, setTransferNote] = React.useState("");
  const [followUpDialogOpen, setFollowUpDialogOpen] = React.useState(false);
  const [followUpOverride, setFollowUpOverride] = React.useState<any | null>(null);
  const [rescheduleToken, setRescheduleToken] = React.useState("");
  const [intakeDecisionOpen, setIntakeDecisionOpen] = React.useState(false);
  const [intakeNextStatus, setIntakeNextStatus] = React.useState("PENDING_DECISION");
  const extraObj = parseExtra(l?.extra);
  const attributionSummary = l?.attributionSummary || null;
  const leadSourceType = String(
    l?.leadSourceType || attributionSummary?.leadSourceType || extraObj?.leadSource?.type || "PLATFORM_DIRECT"
  )
    .trim()
    .toUpperCase();
  const sourceAgentName =
    String(
      l?.sourceAgentName ||
        attributionSummary?.sourceAgentName ||
        extraObj?.leadSource?.sourceAgentName ||
        ""
    ).trim() || null;
  const followUpSummary = React.useMemo(
    () => getFollowUpSummary(followUpOverride || l, parseExtra(followUpOverride?.extra || l?.extra)),
    [followUpOverride, l],
  );
  const pricingFlags = getPricingFlagsFromExtra(extraObj);
  const concierge = extraObj?.concierge || {};
  const conciergeStatus: string = concierge.status || "REQUESTED";
  const conciergeQueue: string = String(concierge.queue || "").toUpperCase();
  const ownerAgentId =
    concierge.ownerAgentId ??
    l?.ownerAgentId ??
    l?.assignedAgentId ??
    attributionSummary?.assignedAgentId ??
    null;
  const ownedByMe =
    currentAgentId != null &&
    ownerAgentId != null &&
    String(ownerAgentId) === String(currentAgentId);

  const kindLabel = mapKind(l?.kind);
  const payerLabel = mapPayerType(l?.payerType);
  const urgencyLabel = mapUrgency(l?.urgency);

  const leadRef = l?.referrerContact;
  const leadRefEntity = l?.referrer || null;
  const refName = leadRefEntity?.contactName || leadRef?.name || "-";
  const refEmail = leadRefEntity?.email || leadRef?.email || null;
  const refPhone = leadRefEntity?.phone || leadRef?.phone || null;
  const refId = leadRefEntity?.id ?? leadRef?.id ?? null;
  const portalReferenceNumber =
    String(
      l?.residentKey ||
        extraObj?.residentKeyDisplay ||
        extraObj?.patientKey ||
        extraObj?.patientInitials ||
        ""
    ).trim() || "-";

  const referrerBookingsQuery = useQuery(GET_BOOKINGS_BY_EMAIL, {
    variables: {
      email: String(refEmail || "").trim(),
      selectedYear: new Date().getFullYear(),
    },
    skip: !String(refEmail || "").trim(),
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    pollInterval: String(refEmail || "").trim() ? bookingLookupPollMs : 0,
  });
  const referrerBookings = React.useMemo(
    () => (referrerBookingsQuery.data?.getBookingsByEmail ?? []) as any[],
    [referrerBookingsQuery.data?.getBookingsByEmail]
  );
  const activeBookedTour = React.useMemo(
    () => findScheduledTourBookingForLead(l, referrerBookings),
    [l, referrerBookings]
  );
  const activeBookedTourRecord = React.useMemo(() => {
    const bookingId = String(activeBookedTour?.id || "").trim();
    if (!bookingId) return null;
    return (
      referrerBookings.find(
        (booking: any) => String(booking?.id || "").trim() === bookingId,
      ) || null
    );
  }, [activeBookedTour?.id, referrerBookings]);
  const bookedTourForDisplay = React.useMemo(() => {
    const rawStatus = String(l?.status || "").toUpperCase();
    if (TERMINAL_STAGE_SET.has(rawStatus)) return null;
    return activeBookedTour || getTourScheduleFromLead(l);
  }, [activeBookedTour, l]);
  const latestTourCancellation = React.useMemo(
    () => (bookedTourForDisplay ? null : getLatestTourCancellation(l)),
    [bookedTourForDisplay, l]
  );
  const bookedTourLabel = React.useMemo(
    () => formatTourScheduleLabel(bookedTourForDisplay?.bookingTime),
    [bookedTourForDisplay?.bookingTime]
  );

  React.useEffect(() => {
    try {
      const savedReferrer = localStorage.getItem(referrerPanelStorageKey);
      const savedProvider = localStorage.getItem(providerPanelStorageKey);
      setReferrerPanelOpen(savedReferrer == null ? true : savedReferrer === "true");
      setProviderPanelOpen(savedProvider == null ? true : savedProvider === "true");
    } catch {
      setReferrerPanelOpen(true);
      setProviderPanelOpen(true);
    }
  }, [providerPanelStorageKey, referrerPanelStorageKey]);

  const handleReferrerPanelChange = React.useCallback(
    (nextExpanded: boolean) => {
      setReferrerPanelOpen(nextExpanded);
      try {
        localStorage.setItem(referrerPanelStorageKey, String(nextExpanded));
      } catch {}
    },
    [referrerPanelStorageKey],
  );

  const handleProviderPanelChange = React.useCallback(
    (nextExpanded: boolean) => {
      setProviderPanelOpen(nextExpanded);
      try {
        localStorage.setItem(providerPanelStorageKey, String(nextExpanded));
      } catch {}
    },
    [providerPanelStorageKey],
  );

  const providerName = l?.business?.name || "-";
  const providerBusinessId = l?.businessId ?? l?.business?.id ?? null;
  const providerEmail = l?.business?.emails?.[0] || null;
  const providerPhone =
    l?.providerContact?.phone ||
    extraObj?.providerContact?.phone ||
    l?.business?.phone ||
    null;

  const isConcierge = pricingFlags.mode === "CONCIERGE";
  const providerStatusRaw =
    l?.providerContact?.status ||
    extraObj?.providerContact?.status ||
    "PENDING_PROVIDER";
  const providerStatus = String(providerStatusRaw || "").toUpperCase();
  const providerStatusLabel =
    providerStatus === "UNLOCKED"
      ? "Request Accepted"
      : providerStatus === "INVITED"
      ? "Request Sent"
      : providerStatus === "PENDING_PROVIDER"
      ? "Pending Provider"
      : String(providerStatusRaw || providerStatus);

  const resolvedLeadStatus = React.useMemo(() => {
    const rawStatus = String(l?.status || "").toUpperCase();
    if (TERMINAL_STAGE_SET.has(rawStatus)) return rawStatus;
    if (bookedTourForDisplay) return "TOUR_SCHEDULED";
    if (rawStatus === "TOUR_SCHEDULED") {
      return providerStatus === "UNLOCKED" ? "QUALIFYING" : "NEW";
    }
    return rawStatus || "NEW";
  }, [bookedTourForDisplay, l?.status, providerStatus]);
  const leadStageLabel =
    STAGE_LABELS[resolvedLeadStatus] || resolvedLeadStatus || "Unknown";
  const cardTone = getLeadCardTone(resolvedLeadStatus);
  const canShareTourCalendar = providerStatus === "UNLOCKED";

  const pickTourBooking = React.useCallback((items: any[] = []) => {
    const normalized = items
      .map((item: any) => ({
        id: String(item?.id ?? ""),
        type: "plany365" as const,
        url: String(item?.link || ""),
        allowEmbed: item?.allowEmbed !== false,
        createdByAgentId:
          item?.createdByAgentId != null ? String(item.createdByAgentId) : null,
        title: String(item?.title || ""),
      }))
      .filter((item: any) => item.id && item.url);

    return (
      normalized
        .map((item: any) => {
          const haystack = `${item.title} ${item.url}`.toLowerCase();
          let score = 0;
          if (/tour|visit|walkthrough/.test(haystack)) score += 4;
          if (item.createdByAgentId) score += 1;
          return { ...item, score };
        })
        .sort((a: any, b: any) => b.score - a.score)[0] || null
    );
  }, []);

  const bizSlug = l?.business?.url || "";
  const { data: btData } = useQuery(GET_BUSINESS_BOOKING_TYPES, {
    variables: { businessUrl: bizSlug },
    skip: !bizSlug,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const selectedTourBooking = React.useMemo(
    () => pickTourBooking(btData?.getBusinessBookingTypes ?? []),
    [btData?.getBusinessBookingTypes, pickTourBooking]
  );
  const businessBookings: BusinessBookingConfig[] = selectedTourBooking
    ? [
        {
          id: selectedTourBooking.id,
          type: selectedTourBooking.type,
          url: selectedTourBooking.url,
          allowEmbed: selectedTourBooking.allowEmbed,
          createdByAgentId: selectedTourBooking.createdByAgentId,
        },
      ]
    : [];

  const canClaim = isConcierge && canActAsAgent && !ownerAgentId;
  const canUnclaim = isConcierge && canActAsAgent && ownedByMe;
  const showGroupManagementActions = !suppressGroupContext;
  const assignedToAnotherAgent = isConcierge && !!ownerAgentId && !ownedByMe;
  const canUseReferrerActions = !isConcierge || (canActAsAgent && ownedByMe);
  const canMarkReferrerContacted =
    canActAsAgent && ownedByMe && String(l?.status || "").toUpperCase() === "NEW";
  const canCompleteIntake =
    isConcierge &&
    canActAsAgent &&
    ["ASSIGNED", "INTAKE_DONE"].includes(String(conciergeStatus || "").toUpperCase()) &&
    ownedByMe &&
    !TERMINAL_STAGE_SET.has(String(l?.status || "").toUpperCase());
  const hasBookedTour = Boolean(activeBookedTour);
  const hasScheduledTour = Boolean(bookedTourForDisplay);
  const referrerActionDisabledReason = !canUseReferrerActions
    ? !canActAsAgent
      ? "Agent actions are disabled for admin-only accounts."
      : assignedToAnotherAgent
      ? "This case is assigned to another agent."
      : "Claim this case first to chat with the referrer or enable tour booking."
    : hasScheduledTour
    ? "A tour is already booked for this case."
    : providerStatus !== "UNLOCKED"
    ? "Enable tour booking after the provider accepts the referral agreement."
    : !selectedTourBooking?.id
    ? "Create or connect a tour booking type for this provider first."
    : null;

  const [followUpForm, setFollowUpForm] = React.useState({
    followUpStatus: followUpSummary.followUpStatus || "PENDING_ASSIGNMENT",
    nextFollowUpAt: toDateTimeLocalInput(followUpSummary.nextFollowUpAt),
    lastFollowUpAt: toDateTimeLocalInput(followUpSummary.lastFollowUpAt),
    followUpNotes: followUpSummary.followUpNotes || "",
  });

  React.useEffect(() => {
    setFollowUpForm({
      followUpStatus: followUpSummary.followUpStatus || "PENDING_ASSIGNMENT",
      nextFollowUpAt: toDateTimeLocalInput(followUpSummary.nextFollowUpAt),
      lastFollowUpAt: toDateTimeLocalInput(followUpSummary.lastFollowUpAt),
      followUpNotes: followUpSummary.followUpNotes || "",
    });
  }, [
    followUpSummary.followUpNotes,
    followUpSummary.followUpStatus,
    followUpSummary.lastFollowUpAt,
    followUpSummary.nextFollowUpAt,
  ]);

  const bookingUrl = selectedTourBooking?.url
    ? `${rootClientPath}/${bizSlug}/booking/${selectedTourBooking.url}?${new URLSearchParams({
        referralLeadId: String(l.id),
        coordinatorEmail: String(providerUser?.email || ""),
        coordinatorName: String(providerUser?.username || ""),
        name: String(refName || ""),
        email: String(refEmail || ""),
        phone: String(refPhone || ""),
      }).toString()}`
    : null;
  const scheduledManageHref = React.useMemo(() => {
    if (!hasScheduledTour) return null;
    const params = new URLSearchParams();
    const email = String(refEmail || "").trim();
    const referralLeadId = String(l?.id || "").trim();
    const bookingId = String(bookedTourForDisplay?.id || "").trim();
    const bookingTime = String(bookedTourForDisplay?.bookingTime || "").trim();
    if (email) params.set("email", email);
    if (referralLeadId) params.set("referralLeadId", referralLeadId);
    if (bookingId) params.set("bookingId", bookingId);
    if (bookingTime) {
      const dt = new Date(bookingTime);
      if (!Number.isNaN(dt.getTime())) {
        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, "0");
        params.set("month", `${year}-${month}-01`);
      }
    }
    return `/myschedule${params.toString() ? `?${params.toString()}` : ""}`;
  }, [bookedTourForDisplay?.bookingTime, bookedTourForDisplay?.id, hasScheduledTour, l?.id, refEmail]);

  const transferChoices = React.useMemo(
    () =>
      agentOptions.filter(
        (option) => String(option.id || "") && String(option.id) !== String(ownerAgentId || ""),
      ),
    [agentOptions, ownerAgentId],
  );
  const assignedAgent = React.useMemo(
    () =>
      agentOptions.find(
        (option) => String(option.id || "") === String(ownerAgentId || ""),
      ) || null,
    [agentOptions, ownerAgentId],
  );
  const assignedAgentLabel =
    assignedAgent?.name ||
    String(l?.ownerAgentName || "").trim() ||
    String(l?.assignedAgentName || "").trim() ||
    String(attributionSummary?.assignedAgentName || "").trim() ||
    String(concierge?.ownerAgentName || "").trim() ||
    (ownerAgentId ? `Agent ${ownerAgentId}` : "");

  const handleTransferSubmit = async () => {
    if (!transferTargetId || !l.__onTransfer) return;
    const resp = await l.__onTransfer(transferTargetId, transferNote);
    if (resp?.data?.transferConciergeCase?.success === false) return;
    setTransferOpen(false);
    setTransferTargetId("");
    setTransferNote("");
  };

  const handleFollowUpSubmit = async () => {
    const payload = {
      followUpStatus: followUpForm.followUpStatus,
      nextFollowUpAt: followUpForm.nextFollowUpAt
        ? new Date(followUpForm.nextFollowUpAt).toISOString()
        : null,
      lastFollowUpAt: followUpForm.lastFollowUpAt
        ? new Date(followUpForm.lastFollowUpAt).toISOString()
        : null,
      followUpNotes: followUpForm.followUpNotes.trim() || null,
    };
    const resp = await updateFollowUp({
      variables: {
        leadId: l.id,
        input: payload,
      },
    });
    const updated = resp?.data?.updateConciergeLeadFollowUp;
    if (updated) {
      setFollowUpOverride({
        ...l,
        ...updated,
      });
    }
    setFollowUpDialogOpen(false);
  };

  const handleCompleteIntakeDecision = async () => {
    await l.__onIntakeDone();
    if (l.__onChangeLeadStatus && intakeNextStatus) {
      await l.__onChangeLeadStatus(intakeNextStatus);
    }
    setIntakeDecisionOpen(false);
  };

  const sendTourCancellationEmails = React.useCallback(
    async (booking: any) => {
      if (!booking) return;

      const bookingId = Number(booking?.id || 0);
      const meetingTime = formatMeetingTimeForNotice(
        booking?.time,
        providerUser?.timeZone?.timezone || booking?.timezone || null,
      );
      const bookingTitle =
        String(booking?.bookingTypeName || booking?.quickTitle || "Tour").trim() || "Tour";
      const fromEmail = String(booking?.staffEmail || providerUser?.email || providerEmail || "").trim();
      if (!fromEmail) return;

      const bookingActionLinks = getBookingActionLinks({
        origin: window.location.origin,
        bookingId,
        timeValue: booking?.time,
        email: refEmail || booking?.inviteeEmail || booking?.bookedForEmail || booking?.bookedByEmail || "",
      });

      const participantEmails = Array.from(
        new Set(
          [
            String(refEmail || "").trim().toLowerCase(),
            String(booking?.inviteeEmail || "").trim().toLowerCase(),
            String(booking?.bookedForEmail || "").trim().toLowerCase(),
            String(booking?.bookedByEmail || "").trim().toLowerCase(),
            ...toEmailList(booking?.confirmationEmails),
          ].filter(Boolean),
        ),
      ).filter((email) => email !== String(providerEmail || "").trim().toLowerCase());

      const participantLabel =
        String(booking?.bookedForName || booking?.inviteeName || refName || "the family").trim() ||
        "the family";

      for (const email of participantEmails) {
        await sendEmail({
          variables: {
            from_email: fromEmail,
            to: email,
            subject: "Tour Cancellation Notice",
            text: `Hello,
Your ${bookingTitle} scheduled for ${meetingTime || "the selected time"} has been cancelled.
Provider: ${providerName}
${getBookingActionTextBlock(bookingActionLinks)}
If you want to choose a new tour time, you can reschedule using the link above.`,
            html: `
              <p>Hello,</p>
              <p>
                Your <strong>${bookingTitle}</strong> scheduled for
                <strong>${meetingTime || "the selected time"}</strong> has been cancelled.
              </p>
              <p><strong>Provider:</strong> ${providerName}</p>
              ${getBookingActionHtmlBlock({
                ...bookingActionLinks,
                intro: "If you want to book a new time, use the options below.",
              })}
              <p>If you have questions, reply to this message and we will help coordinate the next step.</p>
            `,
          },
        });
      }

      const normalizedProviderEmail = String(providerEmail || "").trim().toLowerCase();
      if (normalizedProviderEmail) {
        await sendEmail({
          variables: {
            from_email: fromEmail,
            to: normalizedProviderEmail,
            subject: "Tour Cancellation Notice",
            text: `Hello,
The scheduled ${bookingTitle} for ${meetingTime || "the selected time"} has been cancelled.
Referrer / family: ${participantLabel}
Case: Lead #${l?.id}
Please hold off on expecting this tour unless a new booking is sent.`,
            html: `
              <p>Hello,</p>
              <p>
                The scheduled <strong>${bookingTitle}</strong> for
                <strong>${meetingTime || "the selected time"}</strong> has been cancelled.
              </p>
              <p><strong>Referrer / family:</strong> ${participantLabel}</p>
              <p><strong>Case:</strong> Lead #${l?.id}</p>
              <p>Please hold off on expecting this tour unless a new booking is sent.</p>
            `,
          },
        });
      }
    },
    [l?.id, providerEmail, providerName, providerUser?.email, providerUser?.timeZone?.timezone, refEmail, refName, sendEmail],
  );

  const handleCancelTour = async () => {
    const bookingId = Number(activeBookedTour?.id || 0);
    if (!bookingId) return;
    if (!window.confirm("Cancel the current tour for this case?")) return;
    await sendTourCancellationEmails(activeBookedTourRecord);
    await removeBookingMutation({ variables: { bookingId } });
    await referrerBookingsQuery.refetch();
  };

  const handleRescheduleTour = async () => {
    const bookingId = Number(activeBookedTour?.id || 0);
    if (!bookingId) return;
    if (!window.confirm("Cancel the current tour and choose a new time now?")) return;
    await sendTourCancellationEmails(activeBookedTourRecord);
    await removeBookingMutation({ variables: { bookingId } });
    await referrerBookingsQuery.refetch();
    setRescheduleToken(String(Date.now()));
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: cardTone.sectionBg,
        boxShadow: "0 8px 24px rgba(15, 31, 46, 0.05)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, sm: 1.5 },
          py: 1,
          bgcolor: cardTone.headerBg,
          borderBottom: "1px solid rgba(103, 114, 126, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1, md: 2 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ width: "100%" }}
        >
          <ChipWrap>
            <Chip size="small" label={residentLabel} variant="outlined" />
            {!suppressGroupContext ? (
              <Chip size="small" label={kindLabel} variant="outlined" />
            ) : null}
            {!suppressGroupContext ? (
              <Chip
              size="small"
              label={`Concierge • ${conciergeStatus}${ownedByMe ? " (me)" : ownerAgentId ? "" : " (unassigned)"}`}
              color={
                conciergeStatus === "REQUESTED"
                  ? "warning"
                  : conciergeStatus === "ASSIGNED"
                  ? "info"
                  : "success"
              }
              variant="outlined"
            />
            ) : null}
            {!suppressGroupContext && assignedAgentLabel ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Assigned: ${ownedByMe ? `${assignedAgentLabel} (me)` : assignedAgentLabel}`}
              />
            ) : null}
            {!suppressGroupContext && isConcierge && conciergeQueue === "SCREENING" ? (
              <Chip size="small" label="Screening" color="secondary" variant="outlined" />
            ) : null}
            {!suppressGroupContext ? (
              <Chip size="small" label={`Lead: ${leadStageLabel}`} variant="outlined" />
            ) : null}
            {!suppressGroupContext && leadSourceType === "AGENT_SOURCED" ? (
              <Chip
                size="small"
                label={sourceAgentName ? `Source: ${sourceAgentName}` : "Agent-sourced"}
                variant="outlined"
              />
            ) : null}
            {!suppressGroupContext && payerLabel && payerLabel !== "Unknown" ? (
              <Chip size="small" label={`Payer: ${payerLabel}`} variant="outlined" />
            ) : null}
            {isConcierge && !bookedTourForDisplay ? (
              <Chip
                size="small"
                color={
                  providerStatus === "UNLOCKED"
                    ? "success"
                    : providerStatus === "INVITED"
                    ? "info"
                    : "default"
                }
                label={`Provider: ${providerStatusLabel}`}
                variant="outlined"
              />
            ) : null}
            {bookedTourForDisplay ? (
              <Chip
                size="small"
                color="success"
                variant="filled"
                label={
                  bookedTourLabel
                    ? `Tour Booked • ${bookedTourLabel}`
                    : "Tour Booked"
                }
              />
            ) : null}
          </ChipWrap>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            {showGroupManagementActions && canClaim ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VolunteerActivismIcon />}
                onClick={() => l.__onClaim()}
                disabled={l.__claiming}
              >
                Claim
              </Button>
            ) : null}
            {showGroupManagementActions && !canClaim && !ownedByMe && isConcierge ? (
              <Tooltip title="This lead has already been claimed by another agent.">
                <span>
                  <Button size="small" variant="outlined" startIcon={<VolunteerActivismIcon />} disabled>
                    Claimed
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {showGroupManagementActions && canUnclaim ? (
              <Tooltip title="Release this case so another agent can claim it.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => l.__onUnclaim()}
                    disabled={l.__unclaiming}
                  >
                    Unclaim
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {showGroupManagementActions && canActAsAgent && ownedByMe && Array.isArray(transferChoices) && transferChoices.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setTransferOpen(true)}
              >
                Transfer
              </Button>
            ) : null}
            {canCompleteIntake ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AssignmentTurnedInIcon />}
                onClick={() => {
                  setIntakeNextStatus(bookedTourForDisplay ? "PENDING_DECISION" : "MATCHING");
                  setIntakeDecisionOpen(true);
                }}
                disabled={l.__completing}
              >
                Complete Intake
              </Button>
            ) : null}
            {!canActAsAgent && isConcierge ? (
              <Tooltip title="Agent actions are disabled for admin-only accounts.">
                <span>
                  <Button size="small" variant="outlined" disabled>
                    View Only
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {showGroupManagementActions && isConcierge ? (
              <Button
                size="small"
                variant="outlined"
                href={buildAgentAgreementUrl(rootClientPath, { leadId: l.id })}
              >
                View Agreement
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: { xs: 1.5, sm: 1.75 } }}>
        <Box sx={{ mb: 1.25 }}>
          <PaymentStatusInline leadId={l.id} leadStatus={l.status} />
        </Box>

        <Box
          sx={{
            mb: 1.5,
            px: 1.25,
            py: 1,
            borderRadius: 1.75,
            bgcolor: (t) => alpha(t.palette.background.default, 0.55),
            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.9 }}>
            Referral Stages
          </Typography>
          <ReferralStageTracker status={resolvedLeadStatus} />
        </Box>

        {isConcierge && !bookedTourForDisplay && providerStatus === "UNLOCKED" ? (
          <Alert severity="success" sx={{ mb: 1.25 }}>
            The provider accepted the referral agreement and the referrer can now see provider contact details.
            Next step: contact the referrer to confirm fit and discuss timing. Then share tour booking or schedule a tour for them if both sides want to continue.
          </Alert>
        ) : null}
        {bookedTourForDisplay ? (
          <Alert severity="success" variant="outlined" sx={{ mb: 1.25 }}>
            {bookedTourLabel
              ? `Referrer booked a tour for ${bookedTourLabel}.`
              : "Referrer booked a tour."}
          </Alert>
        ) : null}
        {latestTourCancellation ? (
          <Alert severity="info" variant="outlined" sx={{ mb: 1.25 }}>
            {`The scheduled tour${
              latestTourCancellation.bookingTime
                ? ` for ${formatTourScheduleLabel(latestTourCancellation.bookingTime)}`
                : ""
            }${
              latestTourCancellation.businessName
                ? ` with ${latestTourCancellation.businessName}`
                : ""
            } was canceled${
              latestTourCancellation.canceledByName
                ? ` by ${latestTourCancellation.canceledByName}`
                : latestTourCancellation.canceledByRole
                ? ` by ${latestTourCancellation.canceledByRole.toLowerCase()}`
                : ""
            }.`}
          </Alert>
        ) : null}
        {isConcierge && !bookedTourForDisplay && providerStatus === "UNLOCKED" && !ownedByMe ? (
          <Alert severity="info" sx={{ mb: 1.25 }}>
            {assignedToAnotherAgent
              ? "This case is currently assigned to another agent."
              : "Claim this case to enable tour booking and referrer follow-up actions."}
          </Alert>
        ) : null}
        {isConcierge && !bookedTourForDisplay && ownedByMe && providerStatus === "INVITED" ? (
          <Alert severity="warning" sx={{ mb: 1.25 }}>
            The provider has received the request, but has not accepted it yet.
            The referrer will not see tour booking until the provider accepts.
          </Alert>
        ) : null}
        {isConcierge && !bookedTourForDisplay && ownedByMe && providerStatus === "PENDING_PROVIDER" ? (
          <Alert severity="info" sx={{ mb: 1.25 }}>
            This case is still pending provider delivery. Once the provider receives and accepts it, move on to sharing or scheduling the tour.
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.25 }}>
          {leadSourceType !== "AGENT_SOURCED" ? (
            <Chip size="small" variant="outlined" label="Source: Platform" />
          ) : null}
          {urgencyLabel && urgencyLabel !== "Unknown" ? (
            <Chip size="small" variant="outlined" label={`Urgency: ${urgencyLabel}`} />
          ) : null}
          <Chip size="small" variant="outlined" label={`Provider: ${providerName}`} />
          <Chip
            size="small"
            variant="outlined"
            label={<DateStamp value={l.createdAt} />}
            sx={{
              height: "auto",
              alignItems: "flex-start",
              "& .MuiChip-label": {
                display: "block",
                whiteSpace: "normal",
                paddingTop: 0.75,
                paddingBottom: 0.75,
              },
            }}
          />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 4 }}>
          <Box sx={{ minWidth: 280, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Referrer
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {refName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Reference: {portalReferenceNumber}
          </Typography>
          <ReferrerContactPanel
            referrer={{ name: refName, email: refEmail, phone: refPhone }}
            leadStatus={providerStatus}
            expanded={referrerPanelOpen}
            onChange={handleReferrerPanelChange}
          >
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {canMarkReferrerContacted ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => l.__onMarkReferrerContacted()}
                  disabled={l.__markingContacted}
                >
                  Mark Referrer Contacted
                </Button>
              ) : null}
              {providerBusinessId && hasBookedTour ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label="Tour Already Booked"
                />
              ) : null}
              {providerBusinessId && !hasBookedTour ? (
                <ReferrerReadyToggle
                  bookingTypeId={selectedTourBooking?.id}
                  referrerId={refId}
                  groupId={getLeadBroadcastGroupId(l)}
                  scopeKey={l.id}
                  forceReady={forceTourReady}
                  onEnabled={onTourEnabled}
                  disabled={!canShareTourCalendar || !canUseReferrerActions}
                  disabledReason={referrerActionDisabledReason}
                  referrerEmail={refEmail}
                  referrerName={refName}
                  providerName={providerName}
                  bookingLink={bookingUrl}
                />
              ) : null}
              {refPhone ? (
                <Tooltip
                  title={
                    canUseReferrerActions
                      ? ""
                      : assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first to call the referrer."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={canUseReferrerActions ? `tel:${refPhone}` : undefined}
                      disabled={!canUseReferrerActions}
                    >
                      Call Referrer
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {refEmail ? (
                <Tooltip
                  title={
                    canUseReferrerActions
                      ? ""
                      : assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first to email the referrer."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={canUseReferrerActions ? `mailto:${refEmail}` : undefined}
                      disabled={!canUseReferrerActions}
                    >
                      Email Referrer
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              <Tooltip
                title={
                  canUseReferrerActions
                    ? ""
                    : assignedToAnotherAgent
                      ? "This case is assigned to another agent."
                      : "Claim this case first to chat with the referrer."
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!canUseReferrerActions}
                    onClick={() => l.__setChatReferrer({ id: String(l.id), name: refName })}
                  >
                    Chat with Referrer
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </ReferrerContactPanel>
          </Box>

          <Box sx={{ flex: 1, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Provider
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {providerName}
          </Typography>

          <ProviderContactPanel
            lead={l}
            showProviderContact={true}
            expanded={providerPanelOpen}
            onChange={handleProviderPanelChange}
          >
            <SettlementInline
              lead={{
                id: l.id,
                status: l.status,
                settlementTotalByReferrer: l.settlementTotalByReferrer ?? null,
              }}
              editable={false}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
              {providerBusinessId ? (
                <Tooltip
                  title={
                    !canUseReferrerActions
                      ? assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first before scheduling for the referrer."
                      : hasScheduledTour
                      ? "Use Change or Cancel Tour to manage the booked visit."
                      : !canShareTourCalendar
                      ? "Schedule for Referrer becomes available after the provider accepts the referral agreement."
                      : ""
                  }
                >
                  <span>
                    {hasScheduledTour ? (
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={canUseReferrerActions ? scheduledManageHref || undefined : undefined}
                        disabled={!canUseReferrerActions || !scheduledManageHref}
                      >
                        Change or Cancel Tour
                      </Button>
                    ) : (
                      <TourBookingButton
                        lead={{
                          id: l.id,
                          business: { name: providerName },
                          coordinatorEmail: providerUser?.email ?? null,
                          coordinatorName: providerUser?.username ?? null,
                          referrerContact: {
                            id: refId,
                            name: refName,
                            email: refEmail,
                            phone: refPhone,
                          },
                          providerContact: {
                            name: providerName,
                            email: providerEmail,
                            phone: providerPhone,
                          },
                        }}
                        actor="REFERRER"
                        type="plany365"
                        businessBookings={businessBookings}
                        bizSlug={bizSlug}
                        rootClientPath={rootClientPath}
                        label="Schedule for Referrer"
                        disabled={!canUseReferrerActions || !canShareTourCalendar || removingBooking}
                        onLaunch={() => {}}
                      />
                    )}
                  </span>
                </Tooltip>
              ) : null}
              {providerBusinessId && hasBookedTour ? (
                <TourBookingButton
                  lead={{
                    id: l.id,
                    business: { name: providerName },
                    coordinatorEmail: providerUser?.email ?? null,
                    coordinatorName: providerUser?.username ?? null,
                    referrerContact: {
                      id: refId,
                      name: refName,
                      email: refEmail,
                      phone: refPhone,
                    },
                    providerContact: {
                      name: providerName,
                      email: providerEmail,
                      phone: providerPhone,
                    },
                  }}
                  actor="REFERRER"
                  type="plany365"
                  businessBookings={businessBookings}
                  bizSlug={bizSlug}
                  rootClientPath={rootClientPath}
                  disabled={!canUseReferrerActions || !canShareTourCalendar || removingBooking}
                  autoOpenToken={rescheduleToken}
                  hideButton
                  onLaunch={() => {}}
                />
              ) : null}
              {providerBusinessId && hasBookedTour ? (
                <Tooltip
                  title={
                    !canUseReferrerActions
                      ? assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first before cancelling the scheduled tour."
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={handleCancelTour}
                      disabled={!canUseReferrerActions || removingBooking}
                    >
                      Cancel Tour
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {providerPhone ? (
                <Tooltip
                  title={
                    canUseReferrerActions
                      ? ""
                      : assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first to call the provider."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={canUseReferrerActions ? `tel:${providerPhone}` : undefined}
                      disabled={!canUseReferrerActions}
                    >
                      Call Provider
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {providerEmail ? (
                <Tooltip
                  title={
                    canUseReferrerActions
                      ? ""
                      : assignedToAnotherAgent
                        ? "This case is assigned to another agent."
                        : "Claim this case first to email the provider."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={canUseReferrerActions ? `mailto:${providerEmail}` : undefined}
                      disabled={!canUseReferrerActions}
                    >
                      Email Provider
                    </Button>
                  </span>
                </Tooltip>
              ) : null}

              <Button
                size="small"
                variant="outlined"
                disabled={!canUseReferrerActions}
                onClick={() => l.__setChatProvider({ id: String(l.id), name: providerName })}
              >
                Chat with Provider
              </Button>
            </Stack>
          </ProviderContactPanel>
          </Box>

          {l.desiredDate ? (
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Desired
              </Typography>
              <DateStamp value={l.desiredDate || null} />
            </Box>
          ) : null}
        </Stack>
      </Box>
      <Dialog open={followUpDialogOpen} onClose={() => setFollowUpDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Follow-up</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Follow-up status"
              value={followUpForm.followUpStatus}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, followUpStatus: event.target.value }))
              }
              fullWidth
            >
              <MenuItem value="PENDING_ASSIGNMENT">Pending assignment</MenuItem>
              <MenuItem value="NEEDS_OUTREACH">Needs outreach</MenuItem>
              <MenuItem value="WAITING_ON_REFERRER">Waiting on referrer</MenuItem>
              <MenuItem value="WAITING_ON_PROVIDER">Waiting on provider</MenuItem>
              <MenuItem value="FOLLOW_UP_SCHEDULED">Follow-up scheduled</MenuItem>
              <MenuItem value="COMPLETE">Complete</MenuItem>
            </TextField>
            <TextField
              label="Next follow-up"
              type="datetime-local"
              value={followUpForm.nextFollowUpAt}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, nextFollowUpAt: event.target.value }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Last follow-up"
              type="datetime-local"
              value={followUpForm.lastFollowUpAt}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, lastFollowUpAt: event.target.value }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Follow-up notes"
              value={followUpForm.followUpNotes}
              onChange={(event) =>
                setFollowUpForm((prev) => ({ ...prev, followUpNotes: event.target.value }))
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFollowUpSubmit} disabled={updatingFollowUp}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={intakeDecisionOpen}
        onClose={() => setIntakeDecisionOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Complete Intake</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Choose the next case outcome after intake is complete.
            </Typography>
            <TextField
              select
              label="Next status"
              value={intakeNextStatus}
              onChange={(event) => setIntakeNextStatus(event.target.value)}
              fullWidth
            >
              <MenuItem value="MATCHING">Continue Matching</MenuItem>
              <MenuItem value="PENDING_DECISION">Waiting for Decision</MenuItem>
              <MenuItem value="PLACED">Placed / Won</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="LOST">Lost</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntakeDecisionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCompleteIntakeDecision}
            disabled={l.__completing}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Transfer Case</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Target agent"
              value={transferTargetId}
              onChange={(event) => setTransferTargetId(event.target.value)}
              fullWidth
            >
              {transferChoices.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                  {option.email ? ` (${option.email})` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Transfer note"
              value={transferNote}
              onChange={(event) => setTransferNote(event.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransferSubmit}
            disabled={!transferTargetId || l.__transferring}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export {};
